import React, { useState } from 'react';
import { Box, Container, Typography, Grid, List, ListItem, ListItemText, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider, useTheme, useMediaQuery, Menu, MenuItem, IconButton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ContactBanner, SiteFooter } from './Home';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Composant de navigation avec onglets
const NavigationBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [scolariteMenuAnchor, setScolariteMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleScolariteMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setScolariteMenuAnchor(event.currentTarget);
  };

  const handleScolariteMenuClose = () => {
    setScolariteMenuAnchor(null);
  };

  const handleContactsClick = () => {
    const footerElement = document.getElementById('footer');
    if (footerElement) {
      footerElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigationItems = [
    { label: 'ACCUEIL', path: '/' },
    { label: 'PRESENTATION', path: '/presentation' },
    {
      label: 'SCOLARITE',
      path: '/scolarite',
      hasSubmenu: true,
      submenu: [
        { label: 'MATERNELLE', path: '/scolarite' },
        { label: 'CRÈCHE/GARDERIE', path: '/scolarite-maternel' }
      ]
    },
    { label: 'NOS ACTIVITES', path: '/activites' },
    { label: 'BLOG', path: '/blog' },
    { label: 'CONTACTS', path: '#footer', isScroll: true },
    { label: 'CONNEXION', path: '/login' },
  ];

  return (
    <Box
      position="absolute"
      sx={{
        bgcolor: 'transparent',
        boxShadow: 'none',
        zIndex: 10,
        top: '60px',
        width: '100%'
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{
          display: 'flex',
          justifyContent: { xs: 'flex-end', md: 'space-between' },
          alignItems: 'center',
          py: { xs: 1, md: 2 },
          px: { xs: 2, md: 0 },
          minHeight: { xs: 56, md: 64 }
        }}>
          {/* Logo - Masqué sur mobile */}
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box sx={{
              width: 60,
              height: 60,
              border: '2px solid #87CEEB',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'white',
              overflow: 'hidden',
              p: 0.5,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <img
                src="/img/pages/logologo.webp"
                alt="La Maison des Enfants La petite Académie"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </Box>
          </Box>

          {/* Navigation Desktop */}
          {!isMobile && (
            <Box sx={{
              display: 'flex',
              gap: { md: 2, lg: 3 },
              alignItems: 'center'
            }}>
              {navigationItems.map((item) => (
                <Box key={item.label}>
                  {item.hasSubmenu ? (
                    <>
                      <Typography
                        onClick={handleScolariteMenuOpen}
                        sx={{
                          color: item.label === 'SCOLARITE' ? '#FF9800' : 'white',
                          fontWeight: 600,
                          fontSize: { md: 12, lg: 14 },
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          px: { md: 1.5, lg: 2 },
                          py: 1,
                          borderBottom: item.label === 'SCOLARITE' ? '2px solid #FF9800' : '2px solid transparent',
                          cursor: 'pointer',
                          '&:hover': {
                            color: '#FF9800',
                            borderBottom: '2px solid #FF9800',
                          }
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Menu
                        anchorEl={scolariteMenuAnchor}
                        open={Boolean(scolariteMenuAnchor)}
                        onClose={handleScolariteMenuClose}
                        sx={{
                          '& .MuiPaper-root': {
                            bgcolor: 'rgba(23, 128, 194, 0.95)',
                            color: 'white',
                            mt: 1,
                            backdropFilter: 'blur(10px)',
                            minWidth: 200
                          }
                        }}
                      >
                        {item.submenu?.map((subItem) => (
                          <MenuItem
                            key={subItem.label}
                            component={RouterLink}
                            to={subItem.path}
                            onClick={handleScolariteMenuClose}
                            sx={{
                              color: subItem.path === '/scolarite-maternel' ? '#FF9800' : 'white',
                              fontWeight: 600,
                              fontSize: 14,
                              textTransform: 'uppercase',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                              }
                            }}
                          >
                            {subItem.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  ) : item.isScroll ? (
                    <Typography
                      onClick={handleContactsClick}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: { md: 12, lg: 14 },
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        px: { md: 1.5, lg: 2 },
                        py: 1,
                        borderBottom: '2px solid transparent',
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#FF9800',
                          borderBottom: '2px solid #FF9800',
                        }
                      }}
                    >
                      {item.label}
                    </Typography>
                  ) : (
                    <Typography
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: { md: 12, lg: 14 },
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        px: { md: 1.5, lg: 2 },
                        py: 1,
                        borderBottom: '2px solid transparent',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        '&:hover': {
                          color: '#FF9800',
                          borderBottom: '2px solid #FF9800',
                        }
                      }}
                    >
                      {item.label}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Menu Mobile */}
          {isMobile && (
            <>
              <IconButton
                color="inherit"
                onClick={handleMobileMenuOpen}
                sx={{ color: 'white' }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                sx={{
                  '& .MuiPaper-root': {
                    bgcolor: 'rgba(23, 128, 194, 0.95)',
                    color: 'white',
                    mt: 1,
                    backdropFilter: 'blur(10px)'
                  }
                }}
              >
                {navigationItems.map((item) => (
                  <Box key={item.label}>
                    {item.hasSubmenu ? (
                      <>
                        <MenuItem
                          onClick={handleScolariteMenuOpen}
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                            fontSize: 14,
                            textTransform: 'uppercase',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          {item.label}
                          <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: 16 }} />
                        </MenuItem>
                        <Menu
                          anchorEl={scolariteMenuAnchor}
                          open={Boolean(scolariteMenuAnchor)}
                          onClose={handleScolariteMenuClose}
                          sx={{
                            '& .MuiPaper-root': {
                              bgcolor: 'rgba(23, 128, 194, 0.95)',
                              color: 'white',
                              mt: 1,
                              backdropFilter: 'blur(10px)',
                              minWidth: 200
                            }
                          }}
                        >
                          {item.submenu?.map((subItem) => (
                            <MenuItem
                              key={subItem.label}
                              component={RouterLink}
                              to={subItem.path}
                              onClick={() => {
                                handleScolariteMenuClose();
                                handleMobileMenuClose();
                              }}
                              sx={{
                                color: subItem.path === '/scolarite-maternel' ? '#FF9800' : 'white',
                                fontWeight: 600,
                                fontSize: 14,
                                textTransform: 'uppercase',
                                '&:hover': {
                                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                                }
                              }}
                            >
                              {subItem.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    ) : item.isScroll ? (
                      <MenuItem
                        onClick={() => {
                          handleContactsClick();
                          handleMobileMenuClose();
                        }}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: 14,
                          textTransform: 'uppercase',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    ) : (
                      <MenuItem
                        component={RouterLink}
                        to={item.path}
                        onClick={handleMobileMenuClose}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: 14,
                          textTransform: 'uppercase',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    )}
                  </Box>
                ))}
              </Menu>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

// Section Hero pour la page Scolarité
const ScolariteHero = () => {
  return (
    <Box sx={{
      width: '100%',
      height: '100vh',
      position: 'relative',
      bgcolor: '#1780c2',
      overflow: 'hidden'
    }}>
      {/* Image de fond */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url(/img/pages/ac.jpg)', // Image de l'école
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        zIndex: 1
      }} />

      {/* Overlay pour la lisibilité */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(0, 0, 0, 0.3)',
        zIndex: 2
      }} />

      {/* Contenu textuel */}
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 3,
        textAlign: 'center',
        width: { xs: '90%', md: '80%' }
      }}>
        {/* Titre principal - "SCOLARITÉ" */}
        <Typography
          variant="h1"
          sx={{
            color: 'white',
            fontWeight: 'bold',
            fontSize: { xs: 40, md: 60, lg: 80 },
            textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
            mb: 2,
            textTransform: 'uppercase',
            letterSpacing: 2
          }}
        >
          SCOLARITÉ
        </Typography>

        {/* Bannière bleue - "MATERNELLE" */}
        <Box sx={{
          bgcolor: '#1780c2',
          color: 'white',
          py: { xs: 1, md: 1.5 },
          px: { xs: 2, md: 3 },
          borderRadius: 1,
          mb: 2,
          display: 'inline-block',
          border: '2px solid white'
        }}>
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: { xs: 18, md: 24, lg: 28 },
              textTransform: 'uppercase',
              letterSpacing: 1
            }}
          >
            CRÈCHE/GARDERIE
          </Typography>
        </Box>

        {/* Sous-titre - "ÉDUCATION PRÉSCOLAIRE" */}

      </Box>
    </Box>
  );
};

// Section de contenu pour la crèche/garderie
const CrecheGarderieContent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{
      bgcolor: 'white',
      py: { xs: 6, md: 8 },
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Motifs décoratifs en arrière-plan */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url(/img/pages/doodles.png)',
        backgroundSize: '200px',
        backgroundRepeat: 'repeat',
        opacity: 0.05,
        zIndex: 0
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center">
          {/* Image */}
          <Grid item xs={12} md={6}>
            <Box sx={{
              position: 'relative',
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              '&:hover': {
                boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
                transition: 'box-shadow 0.3s ease'
              }
            }}>
              <img
                src="/img/pages/creche-garderie.jpeg"
                alt="Crèche Garderie - La Petite Académie"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  console.error('Erreur de chargement de l\'image:', e);
                  // Fallback si l'image ne charge pas
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </Box>
          </Grid>

          {/* Texte */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  color: '#1780c2',
                  fontWeight: 700,
                  fontSize: { xs: 28, md: 36, lg: 42 },
                  mb: 3,
                  fontFamily: 'Arial, sans-serif'
                }}
              >
                Bienvenue dans notre crèche/garderie !
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: '#333',
                  fontSize: { xs: 16, md: 18 },
                  lineHeight: 1.8,
                  mb: 3,
                  fontFamily: 'Arial, sans-serif'
                }}
              >
                Nous accueillons les enfants de 3 mois à 2 ans et demi dans un cadre sécurisé, chaleureux et adapté à chaque âge, où ils découvrent, explorent et grandissent à leur rythme.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: '#333',
                  fontSize: { xs: 16, md: 18 },
                  lineHeight: 1.8,
                  mb: 3,
                  fontFamily: 'Arial, sans-serif'
                }}
              >
                À travers des activités ludiques et éducatives, nous accompagnons pas à pas leur éveil, leur curiosité et leur autonomie, en tissant chaque jour un lien de confiance avec les familles.
              </Typography>

              <Box sx={{
                bgcolor: '#f8f9fa',
                borderLeft: '4px solid #1780c2',
                p: 2,
                borderRadius: 1,
                mt: 3
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    fontSize: { xs: 14, md: 16 },
                    fontStyle: 'italic',
                    fontFamily: 'Arial, sans-serif'
                  }}
                >
                  <strong>NB :</strong> Notre crèche garderie est ouverte pendant toute l'année scolaire.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Section Liste des pièces à fournir et Horaires */}
        <Grid container spacing={4} sx={{ mt: 4 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{
              bgcolor: '#f8f9fa',
              borderRadius: 3,
              p: { xs: 3, md: 4 },
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
              height: '100%'
            }}>
              <Typography
                variant="h3"
                sx={{
                  color: '#1780c2',
                  fontWeight: 700,
                  fontSize: { xs: 22, md: 28 },
                  mb: 3,
                  fontFamily: 'Arial, sans-serif',
                  textTransform: 'uppercase'
                }}
              >
                LISTE DES PIÈCES À FOURNIR
              </Typography>
              <List sx={{ pl: 0 }}>
                <ListItem sx={{ px: 0, py: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                  <ListItemText
                    primary="Une copie de l'extrait de naissance"
                    primaryTypographyProps={{
                      fontSize: { xs: 15, md: 16 },
                      color: '#333',
                      fontFamily: 'Arial, sans-serif'
                    }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                  <ListItemText
                    primary="3 photos d'identité récentes de même tirage"
                    primaryTypographyProps={{
                      fontSize: { xs: 15, md: 16 },
                      color: '#333',
                      fontFamily: 'Arial, sans-serif'
                    }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                  <ListItemText
                    primary="1 Certificat de vaccinations original à jour signé par le pédiatre"
                    primaryTypographyProps={{
                      fontSize: { xs: 15, md: 16 },
                      color: '#333',
                      fontFamily: 'Arial, sans-serif'
                    }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1.5 }}>
                  <ListItemText
                    primary="1 Fiche d'Identification et 1 fiche Sanitaire à récupérer à la Petite Académie"
                    primaryTypographyProps={{
                      fontSize: { xs: 15, md: 16 },
                      color: '#333',
                      fontFamily: 'Arial, sans-serif'
                    }}
                  />
                </ListItem>
              </List>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{
              bgcolor: '#1780c2',
              borderRadius: 3,
              p: { xs: 3, md: 4 },
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <Typography
                variant="h3"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  fontSize: { xs: 22, md: 28 },
                  mb: 3,
                  fontFamily: 'Arial, sans-serif',
                  textTransform: 'uppercase'
                }}
              >
                HORAIRE
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'white',
                  fontSize: { xs: 18, md: 20 },
                  fontWeight: 600,
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: 1.8
                }}
              >
                Lundi au vendredi
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'white',
                  fontSize: { xs: 16, md: 18 },
                  fontFamily: 'Arial, sans-serif',
                  mt: 2,
                  lineHeight: 1.8
                }}
              >
                07h00 - 11h30
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'white',
                  fontSize: { xs: 16, md: 18 },
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: 1.8
                }}
              >
                et
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'white',
                  fontSize: { xs: 16, md: 18 },
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: 1.8
                }}
              >
                13h30 - 18h
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};



// Composant principal Scolarité
const ScolariteMaternel = () => {
  return (
    <>
      <Helmet>
        <title>École Maternelle Bilingue Abidjan - La Petite Académie | Méthode Montessori</title>
        <meta name="description" content="École maternelle bilingue à Abidjan Cocody Riviera 3. La Petite Académie propose une pédagogie Montessori pour enfants de 3 à 6 ans avec programme français-anglais, éveil intellectuel et développement de l'autonomie. Inscription 2025-2026." />
        <meta name="keywords" content="école maternelle Abidjan, maternelle bilingue Cocody, méthode Montessori maternelle, programme français-anglais, éveil intellectuel, développement autonomie, inscription maternelle 2025, La Petite Académie, école privée Riviera 3, pédagogie alternative" />
        <meta property="og:title" content="École Maternelle Bilingue Abidjan - La Petite Académie | Méthode Montessori" />
        <meta property="og:description" content="École maternelle bilingue à Abidjan Cocody Riviera 3. La Petite Académie propose une pédagogie Montessori pour enfants de 3 à 6 ans avec programme français-anglais." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lapetiteacademie.ci/scolarite-maternel" />
        <link rel="canonical" href="https://lapetiteacademie.ci/scolarite-maternel" />
      </Helmet>
      <Box sx={{
        width: '100%',
        margin: 0,
        padding: 0
      }}>
        <ContactBanner
          showMobileMenu={true}
          mobileMenuAnchor={null}
          handleMobileMenuOpen={() => { }}
          handleMobileMenuClose={() => { }}
          navigationItems={[
            { label: 'ACCUEIL', path: '/' },
            { label: 'PRESENTATION', path: '/presentation' },
            {
              label: 'SCOLARITE',
              path: '/scolarite',
              hasSubmenu: true,
              submenu: [
                { label: 'MATERNELLE', path: '/scolarite' },
                { label: 'CRÈCHE/GARDERIE', path: '/scolarite-maternel' }
              ]
            },
            { label: 'NOS ACTIVITES', path: '/activites' },
            { label: 'BLOG', path: '/blog' },
            { label: 'CONTACTS', path: '#footer', isScroll: true },
            { label: 'CONNEXION', path: '/login' },
          ]}
        />
        <NavigationBar />
        <ScolariteHero />
        <CrecheGarderieContent />

        <SiteFooter />
      </Box>
    </>
  );
};


export default ScolariteMaternel;
